import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function ChatConstraints() {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const { language, t } = useLanguage();
  const [questions, setQuestions] = useState([]);
  const [baseline, setBaseline] = useState({});
  const [freeform, setFreeform] = useState("");
  const [history, setHistory] = useState([]); // [{role, content}]
  const [changes, setChanges] = useState([]); // [{question, answer}]
  const [clarifications, setClarifications] = useState([]);
  const [constraints, setConstraints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [finalizeResult, setFinalizeResult] = useState(null);
  const [lastExtracted, setLastExtracted] = useState(null);
  const [clearResult, setClearResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get('/api/chat/questions', {
          params: { language }
        });
        if (data.success) setQuestions(data.questions || []);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, [language]);

  const handleBaselineChange = (id, value) => {
    setBaseline(prev => ({ ...prev, [id]: value }));
  };

  const handleAsk = async () => {
    setLoading(true);
    setError(null);
    setFinalizeResult(null);
    try {
      const body = {
        baseline,
        freeform,
        history,
        changes: changes.length ? changes.map(c => ({ q: c.q, a: c.a })) : undefined,
        language
      };
      const { data } = await axios.post('/api/chat/compile', body);
      if (!data.success) throw new Error(data.detail || data.message || 'Chat compile failed');

      setConstraints(data.constraints || []);
      const cls = data.clarifications || [];
      setClarifications(cls);

      const userMsg = {
        role: 'user',
        content: JSON.stringify({ baseline, freeform, changes }, null, 2)
      };
      const assistantMsg = {
        role: 'assistant',
        content: JSON.stringify({ constraints: data.constraints, clarifications: data.clarifications }, null, 2)
      };
      setHistory(prev => [...prev, userMsg, assistantMsg]);

      if (cls.length === 0) {
        setChanges([]);
      } else {
        setChanges(cls.map((q, i) => ({ id: i, q, a: '' })));
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  const handleChangeAnswer = (idx, value) => {
    setChanges(prev => prev.map((c, i) => i === idx ? { ...c, a: value } : c));
  };

  const handleSaveAndGenerate = async () => {
    // One-click: extract & save data, compile constraints, finalize & generate
    setLoading(true);
    setError(null);
    setFinalizeResult(null);
    try {
      // 1) Extract & Save data (employees/shifts/availability/coverage)
      const { data: extractRes } = await axios.post('/api/test-prompt', { 
        prompt: freeform, 
        confirm_and_save: true,
        baseline: baseline,
        language
      });
      if (!extractRes.success) throw new Error(extractRes.message || 'Extraction failed');
      const extracted = extractRes?.schedule?.extracted_data || null;
      setLastExtracted(extracted);

      // 2) Compile constraints from current chat state
      const compileBody = {
        baseline,
        freeform,
        history,
        changes: changes.length ? changes.map(c => ({ q: c.q, a: c.a })) : undefined,
        language
      };
      const { data: compileRes } = await axios.post('/api/chat/compile', compileBody);
      if (!compileRes.success) throw new Error(compileRes.detail || compileRes.message || 'Compile failed');
      const updatedConstraints = compileRes.constraints || [];
      setConstraints(updatedConstraints);
      setClarifications(compileRes.clarifications || []);

      // 3) Finalize: save constraints + create users + generate schedule
      const finalizeBody = {
        constraints: updatedConstraints,
        extracted_data: extracted || undefined,
        create_users: true,
        language
      };
      const { data: finalizeRes } = await axios.post('/api/chat/finalize', finalizeBody);
      if (!finalizeRes.success) throw new Error(finalizeRes.message || 'Finalize failed');

      setFinalizeResult(finalizeRes);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  const handleExtractAndSave = async () => {
    setLoading(true);
    setError(null);
    setFinalizeResult(null);
    try {
      const { data } = await axios.post('/api/test-prompt', { 
        prompt: freeform, 
        confirm_and_save: true,
        baseline: baseline,
        language
      });
      if (!data.success) throw new Error(data.message || 'Extraction failed');
      const extracted = data?.schedule?.extracted_data || null;
      setLastExtracted(extracted);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  const handleApplyDelta = async () => {
    setLoading(true);
    setError(null);
    setFinalizeResult(null);
    try {
      const compileBody = {
        baseline,
        freeform: '',
        history,
        delta: freeform,
        language
      };
      const { data: dataCompile } = await axios.post('/api/chat/compile', compileBody);
      if (!dataCompile.success) throw new Error(dataCompile.detail || dataCompile.message || 'Delta compile failed');

      const updatedConstraints = dataCompile.constraints || [];
      setConstraints(updatedConstraints);
      setClarifications(dataCompile.clarifications || []);
      setHistory(prev => [...prev, { role: 'user', content: `DELTA: ${freeform}` }, { role: 'assistant', content: JSON.stringify(dataCompile) }]);

      const finalizeBody = {
        constraints: updatedConstraints,
        extracted_data: lastExtracted || undefined,
        create_users: true,
        language
      };
      const { data: dataFinalize } = await axios.post('/api/chat/finalize', finalizeBody);
      if (!dataFinalize.success) throw new Error(dataFinalize.message || 'Finalize failed');

      setFinalizeResult(dataFinalize);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  const handleClearTables = async () => {
    setLoading(true);
    setError(null);
    setFinalizeResult(null);
    setClearResult(null);
    try {
      const { data } = await axios.post('/admin/clear-schedule-data', {
        preserve_organizations: true,
        preserve_users: true
      });
      if (!data.success) throw new Error('Clear failed');
      setClearResult(data.cleared || {});
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  const handleInitDatabase = async () => {
    setLoading(true);
    setError(null);
    setFinalizeResult(null);
    setClearResult(null);
    try {
      const { data } = await axios.post('/admin/init-database');
      if (!data.success) throw new Error('Database initialization failed');
      setClearResult({ message: 'Database initialized successfully', details: data.details || {} });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  const handleRestartOptimization = async () => {
    setLoading(true);
    setError(null);
    setFinalizeResult(null);
    try {
      // Save constraints only (without re-extracting data)
      const finalizeBody = {
        constraints: constraints,
        extracted_data: lastExtracted || undefined,
        create_users: false, // Don't create users again
        language
      };
      
      const { data: finalizeRes } = await axios.post('/api/chat/finalize', finalizeBody);
      if (!finalizeRes.success) throw new Error(finalizeRes.message || 'Save constraints failed');

      setFinalizeResult(finalizeRes);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  // Check authentication
  if (!isAuthenticated) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">
            {t('chatConstraints.mustBeLoggedIn')}
          </Alert>
        </Paper>
      </Box>
    );
  }

  if (!isAdmin()) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="warning">
            {t('chatConstraints.needAdminPrivileges')}
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('chatConstraints.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t('chatConstraints.description')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>{t('chatConstraints.baseline')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {questions.map(q => (
            <TextField
              key={q.id}
              label={q.q}
              value={baseline[q.id] || ''}
              onChange={(e) => handleBaselineChange(q.id, e.target.value)}
              fullWidth
            />
          ))}
        </Box>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>{t('chatConstraints.freeform')}</Typography>
        <TextField
          label={t('chatConstraints.freeformLabel')}
          multiline
          rows={4}
          fullWidth
          value={freeform}
          onChange={(e) => setFreeform(e.target.value)}
        />

        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" color="success" onClick={handleSaveAndGenerate} disabled={loading || !freeform.trim()}>
            {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {t('chatConstraints.saveAndFinalize')}
          </Button>
          <Button variant="outlined" onClick={handleRestartOptimization} disabled={loading || constraints.length === 0}>
            {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {t('chatConstraints.saveConstraintsOnly')}
          </Button>
          <Button variant="outlined" color="error" onClick={handleClearTables} disabled={loading}>
            {t('chatConstraints.clearTables')}
          </Button>
          <Button variant="outlined" onClick={handleAsk} disabled={loading}>
            {t('chatConstraints.askUpdate')}
          </Button>
          <Button variant="outlined" color="warning" onClick={handleInitDatabase} disabled={loading}>
            {t('chatConstraints.initDatabase')}
          </Button>
        </Box>

        {clarifications.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>{t('chatConstraints.clarificationsNeeded')}</Typography>
            <List>
              {clarifications.map((q, idx) => (
                <ListItem key={idx} alignItems="flex-start">
                  <ListItemText
                    primary={`Q${idx + 1}: ${q}`}
                    secondary={
                      <TextField
                        fullWidth
                        placeholder={t('chatConstraints.yourAnswer')}
                        value={changes[idx]?.a || ''}
                        onChange={(e) => handleChangeAnswer(idx, e.target.value)}
                      />
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Button variant="contained" onClick={handleAsk} disabled={loading}>
              {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              {t('chatConstraints.submitClarificationAnswers')}
            </Button>
          </Box>
        )}

        {constraints && constraints.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>{t('chatConstraints.constraintsPreview')}</Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#0b102015' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
{JSON.stringify(constraints, null, 2)}
              </pre>
            </Paper>
          </Box>
        )}

        {finalizeResult && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('chatConstraints.setupFinalized')} {finalizeResult.message || t('chatConstraints.setupFinalizedMessage')}
            </Alert>
            {finalizeResult.database_updates && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('chatConstraints.employees')}: {finalizeResult.database_updates.employees_updated || 0}, 
                {t('chatConstraints.shifts')}: {finalizeResult.database_updates.requirements_updated || 0}, 
                {t('chatConstraints.constraints')}: {finalizeResult.constraints_saved || 0}
              </Typography>
            )}
          </Box>
        )}

        {clearResult && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning">
              {t('chatConstraints.clearedTables')} {Object.entries(clearResult).map(([k,v]) => `${k}: ${v}`).join(', ')}
            </Alert>
          </Box>
        )}

        {history.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" gutterBottom>{t('chatConstraints.history')}</Typography>
            {(history.slice(-6)).map((m, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">{m.role}</Typography>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content}</pre>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
